import React from "react";
import { createBrowserRouter, Navigate, Outlet } from "react-router-dom";
import { AddOnProtectedRoute } from "@/components/addon/AddOnProtectedRoute";

import Login from "../components/pages/Login";
import TwoFactorVerify from "../components/pages/TwoFactorVerify";
import TwoFactorEnroll from "../components/pages/TwoFactorEnroll";
import Logout from "../components/pages/Logout";
import Register from "../components/pages/Register";
import OAuthLoginCallback from "@/components/pages/OAuthLoginCallback";
import About from "../components/pages/About";
import FAQ from "../components/pages/FAQ";
import TermsOfUse from "../components/pages/TermsOfUse";
import Privacy from "../components/pages/Privacy";
import MainLayout from "../components/layouts/MainLayout";
import Dashboard from "../components/pages/dashboard/Dashboard";
import DashboardLayout from "../components/layouts/DashboardLayout";
import AccountSettings from "@/components/pages/account/AccountSettings";
import MySettings from "@/components/pages/my-settings/MySettings";
import NotificationsCenter from "@/components/pages/notifications/NotificationsCenter";
import ReferralsPage from "@/components/pages/referrals/ReferralsPage";
import Integrations from "@/components/pages/integrations/Integrations";
import ActivityLogPage from "@/components/pages/activity-log/ActivityLogPage";
import IntegrationDetailPage from "@/components/pages/integrations/IntegrationDetailPage";
import SalesforceCallback from "@/components/pages/integrations/salesforce/SalesforceCallback";
import HubSpotCallback from "@/components/pages/integrations/hubspot/HubSpotCallback";
import GhlCallback from "@/components/pages/integrations/ghl/GhlCallback";
import PodioCallback from "@/components/pages/integrations/podio/PodioCallback";
import Health from "@/components/pages/Health";
import Unsubscribe from "@/components/pages/unsubscribe/Unsubscribe";
import { NewIntegration } from "@/components/pages/integrations/NewIntegration";
import { ContactType } from "@/enum/ContactType";
import LeadDetailPage from "@/components/pages/leads/LeadDetailPage";
import Organisations from "@/components/pages/settings/organisations/Organisations";
import { NewOrganisation } from "@/components/pages/settings/organisations/NewOrganisation";
import ProtectedRoute from "@/components/ProtectedRoute";
import MessagesLayout from "@/components/layouts/MessagesLayout";
import { InboxPage } from "@/components/pages/messages/Inbox";
import TextsReportPage from "@/components/pages/messages/TextsReportPage";
import Calls from "@/components/pages/calls/Calls";
import BillingLayout from "@/components/layouts/BillingLayout";
import BillingHistoryLayout from "@/components/layouts/BillingHistoryLayout";
import { BillingOverview } from "@/components/pages/billing/BillingOverview";
import { PlanTab } from "@/components/pages/billing/PlanTab";
import { UsageTab } from "@/components/pages/billing/UsageTab";
import { TransactionHistory } from "@/components/pages/billing/TransactionHistory";
import { Invoices } from "@/components/pages/billing/Invoices";
import { PaymentSuccess } from "@/components/pages/PaymentSuccess";
import { PaymentFailed } from "@/components/pages/PaymentFailed";
import { AddOns } from "@/components/pages/billing/AddOns";
import CreateCallFlow from "@/components/pages/call-flows/CreateCallFlow";
import EditCallFlow from "@/components/pages/call-flows/EditCallFlow";
import TeamMembers from "@/components/pages/team/TeamMembers";
import OrganisationSettings from "@/components/pages/manage-organisation/OrganisationSettings";
import ExportPage from "@/components/pages/manage-organisation/ExportPage";
import AcceptInvitation from "@/components/pages/AcceptInvitation";
import PlatformAdminRoute from "@/components/PlatformAdminRoute";
import PlatformAdminLayout from "@/components/layouts/PlatformAdminLayout";
import OrganisationsList from "@/components/pages/platform-admin/organisations/OrganisationList";
import OrganisationDetailPage from "@/components/pages/platform-admin/organisations/OrganisationDetailPage";
import WorkflowRunDetailPage from "@/components/pages/platform-admin/activity/WorkflowRunDetailPage";
import UsersList from "@/components/pages/platform-admin/users/UsersList";
import PhoneNumbersList from "@/components/pages/platform-admin/phone-numbers/PhoneNumbersList";
import Analytics from "@/components/pages/platform-admin/analytics/Analytics";
import PlatformSettings from "@/components/pages/platform-admin/settings/PlatformSettings";
import OrganisationCharges from "@/components/pages/platform-admin/organisations/OrganisationCharges";
import LiveLogs from "@/components/pages/platform-admin/logs/LiveLogs";
import PricingManagement from "@/components/pages/platform-admin/pricing/PricingManagement";
import EditPricingPlan from "@/components/pages/platform-admin/pricing/EditPricingPlan";
import PowerDialerPage from "@/components/pages/power-dialer/PowerDialerPage";
import LiveMonitorPage from "@/components/pages/power-dialer/LiveMonitorPage";
import DialerReportsPage from "@/components/pages/power-dialer/DialerReportsPage";
import CampaignListPage from "@/components/pages/campaigns/CampaignListPage";
import CreateCampaignPage from "@/components/pages/campaigns/CreateCampaignPage";
import EditCampaignPage from "@/components/pages/campaigns/EditCampaignPage";
import AiCampaignLivePage from "@/components/pages/campaigns/AiCampaignLivePage";
import CreateContactPage from "@/components/pages/contacts/CreateContactPage";
import ContactListPage from "@/components/pages/contacts/ContactsListPage";
import EditContactPage from "@/components/pages/contacts/EditContactPage";
import ContactListsPage from "@/components/pages/contact-lists/ContactListsPage";
import CreateContactListPage from "@/components/pages/contact-lists/CreateContactListPage";
import EditContactListPage from "@/components/pages/contact-lists/EditContactListPage";
import ContactsLayout from "@/components/layouts/ContactsLayout";
import ContactDetailPage from "@/components/pages/contacts/ContactDetailPage";
import DncManagement from "@/components/pages/dnc/DncManagement";
import ImportHistoryPage from "@/components/pages/contacts/ImportHistoryPage";
import TelephonyPage from "@/components/pages/settings/telephony/TelephonyPage";
import NotFound, { RouteErrorBoundary } from "@/components/pages/NotFound";
import TagsSettingsPage from "@/components/pages/settings/tags/TagsSettingsPage";
import DealStagesSettingsPage from "@/components/pages/settings/deal-stages/DealStagesSettingsPage";
import DealsPage from "@/components/pages/deals/DealsPage";
import DealDetailPage from "@/components/pages/deals/DealDetailPage";
import PropertiesPage from "@/components/pages/properties/PropertiesPage";
import PropertyDetailPage from "@/components/pages/properties/PropertyDetailPage";
import RemoteListingsPage from "@/components/pages/properties/RemoteListingsPage";
import { SitesPage } from "@/components/pages/sites/SitesPage";
import { SiteDetailPage } from "@/components/pages/sites/SiteDetailPage";
import TasksPage from "@/components/pages/tasks/TasksPage";
import WorkflowsPage from "@/components/pages/workflows/WorkflowsPage";
import WorkflowDetailPage from "@/components/pages/workflows/WorkflowDetailPage";
import AiAgentsPage from "@/components/pages/ai-agents/AiAgentsPage";
import AiAgentDetailPage from "@/components/pages/ai-agents/AiAgentDetailPage";
import AiAgentCallsPage from "@/components/pages/ai-agents/AiAgentCallsPage";
import AiMarketplacePage from "@/components/pages/ai-marketplace/AiMarketplacePage";
import AiMarketplaceListingPage from "@/components/pages/ai-marketplace/AiMarketplaceListingPage";
import AiAgentsManagement from "@/components/pages/platform-admin/ai-agents/AiAgentsManagement";
import AiTemplatesAdmin from "@/components/pages/platform-admin/ai-agents/AiTemplatesAdmin";
import AiListingsAdmin from "@/components/pages/platform-admin/ai-agents/AiListingsAdmin";
import DeployedAgentsAdmin from "@/components/pages/platform-admin/ai-agents/DeployedAgentsAdmin";
import PromptsAdmin from "@/components/pages/platform-admin/ai-agents/PromptsAdmin";
import EditAiTemplate from "@/components/pages/platform-admin/ai-agents/EditAiTemplate";
import AiAgentWizard from "@/components/pages/platform-admin/ai-agents/AiAgentWizard";
import ConfigureDeployedAgent from "@/components/pages/platform-admin/ai-agents/ConfigureDeployedAgent";
import AiTestTriggers from "@/components/pages/platform-admin/test/AiTestTriggers";
import AiPerformanceDashboard from "@/components/pages/platform-admin/ai-performance/AiPerformanceDashboard";
import OnboardingGate from "@/components/OnboardingGate";
import SubscriptionGate from "@/components/SubscriptionGate";
import OnboardingWizard from "@/components/pages/onboarding/OnboardingWizard";
import ForgotPassword from "@/components/pages/ForgotPassword";
import ResetPassword from "@/components/pages/ResetPassword";
import ExtensionAuth from "@/components/pages/ExtensionAuth";
import RequestAccess from "@/components/pages/RequestAccess";
import AccessRequestsList from "@/components/pages/platform-admin/access-requests/AccessRequestsList";
import A2pRegistrationsQueue from "@/components/pages/platform-admin/a2p-registrations/A2pRegistrationsQueue";
import TaxExemptionsQueue from "@/components/pages/platform-admin/tax-exemptions/TaxExemptionsQueue";
import EmailBlocklistPage from "@/components/pages/platform-admin/email-blocklist/EmailBlocklistPage";
import CallingRulesPage from "@/components/pages/platform-admin/calling-rules/CallingRulesPage";
import FraudSignalsPage from "@/components/pages/platform-admin/fraud/FraudSignalsPage";
import PlatformActivityPage from "@/components/pages/platform-admin/activity/PlatformActivityPage";
import AuthLayout from "@/components/layouts/AuthLayout";
import { AddOnType } from "@/enum/AddOnType";

const AppNavigation = createBrowserRouter([
  {
    element: <MainLayout />,
    errorElement: <RouteErrorBoundary />,
    children: [
      {
        index: true,
        element: <Navigate to="/dashboard" replace />,
      },
      {
        path: "/faq",
        element: <FAQ />,
      },
      {
        path: "/about",
        element: <About />,
      },
      {
        path: "/terms-of-use",
        element: <TermsOfUse />,
      },
      {
        path: "/privacy-policy",
        element: <Privacy />,
      },
      {
        path: "/health",
        element: <Health />,
      },
      {
        path: "/unsubscribe",
        element: <Unsubscribe />,
      },
      {
        path: "/accept-invitation",
        element: <AcceptInvitation />,
      },
      {
        path: "/logout",
        element: <Logout />,
      },
    ],
  },
  {
    path: "/extension-auth",
    element: <ExtensionAuth />,
  },
  {
    element: <AuthLayout />,
    children: [
      { path: "/login", element: <Login /> },
      { path: "/login/verify", element: <TwoFactorVerify /> },
      { path: "/login/enroll", element: <TwoFactorEnroll /> },
      { path: "/auth/:provider/callback", element: <OAuthLoginCallback /> },
      { path: "/register", element: <Register /> },
      { path: "/request-access", element: <RequestAccess /> },
      { path: "/forgot-password", element: <ForgotPassword /> },
      { path: "/reset-password", element: <ResetPassword /> },
      { path: "*", element: <NotFound /> },
    ],
  },
  {
    element: <ProtectedRoute />,
    children: [
      {
        path: "/onboarding",
        element: <OnboardingWizard />,
      },
      {
        element: <OnboardingGate />,
        children: [
      {
        element: <SubscriptionGate />,
        children: [
          {
        element: <DashboardLayout />,
        children: [
          {
            path: "/dashboard",
            element: <Dashboard />,
          },
          {
            path: "/sites",
            element: <SitesPage />,
          },
          {
            path: "/sites/:id",
            element: <SiteDetailPage />,
          },
          {
            path: "/account",
            element: <AccountSettings />,
          },
          {
            path: "/my-settings",
            element: <MySettings />,
          },
          {
            path: "/notifications",
            element: <NotificationsCenter />,
          },
          {
            path: "/referrals",
            element: <ReferralsPage />,
          },
          {
            path: "/integrations",
            element: <Integrations />,
          },
          {
            path: "/activity",
            element: <ActivityLogPage />,
          },
          {
            path: "/integrations/new",
            element: <NewIntegration />,
          },
          {
            path: "/integrations/:id",
            element: <IntegrationDetailPage />,
          },
          {
            path: "/integrations/salesforce/callback",
            element: <SalesforceCallback />,
          },
          {
            path: "/integrations/hubspot/callback",
            element: <HubSpotCallback />,
          },
          {
            path: "/integrations/podio/callback",
            element: <PodioCallback />,
          },
          {
            path: "/integrations/ghl/callback",
            element: <GhlCallback />,
          },
          {
            path: "/leads",
            element: (
              <ContactListPage
                lockedType={ContactType.LEAD}
                heading="Leads"
                getDetailPath={(leadId) => `/leads/${leadId}`}
              />
            ),
          },
          {
            path: "/leads/:id",
            element: <LeadDetailPage />,
          },
          {
            path: "calls",
            element: <Calls />,
          },
          {
            path: "calls/:callId",
            element: <Calls />,
          },
          {
            path: "/contacts",
            children: [
              { path: ":id/detail", element: <ContactDetailPage /> },
              {
                element: <ContactsLayout />,
                children: [
                  { index: true, element: <Navigate to="/contacts/all" replace /> },
                  { path: "all", element: <ContactListPage excludeType={ContactType.LEAD} /> },
                  { path: "lists", element: <ContactListsPage /> },
                  { path: "new", element: <CreateContactPage /> },
                  { path: ":id/edit", element: <EditContactPage /> },
                  { path: "lists/new", element: <CreateContactListPage /> },
                  { path: "lists/:id/edit", element: <EditContactListPage /> },
                  { path: "dnc", element: <DncManagement /> },
                  { path: "imports", element: <ImportHistoryPage /> },
                ],
              },
            ],
          },
          {
            path: "/inbox",
            element: <MessagesLayout />,
            children: [
              {
                index: true,
                element: <InboxPage />,
              },
              {
                path: "report",
                element: <TextsReportPage />,
              },
              {
                path: "contact/:contactId",
                element: <InboxPage />,
              },
              {
                path: ":conversationId",
                element: <InboxPage />,
              },
            ],
          },
          {
            path: "deals",
            element: <DealsPage />,
          },
          {
            path: "deals/:id",
            element: <DealDetailPage />,
          },
          {
            path: "properties",
            element: <PropertiesPage />,
          },
          {
            path: "properties/listings",
            element: (
              <AddOnProtectedRoute
                addOns={[AddOnType.PROPERTY_DATA_5K, AddOnType.PROPERTY_DATA_10K]}
                title="Remote Listings"
                description="Search and import properties from external data providers. This is the Property Data add-on."
                price="From $49/mo"
              >
                <RemoteListingsPage />
              </AddOnProtectedRoute>
            ),
          },
          {
            path: "properties/:id",
            element: <PropertyDetailPage />,
          },
          {
            path: "tasks",
            element: <TasksPage />,
          },
          {
            path: "ai-agents",
            element: <AiAgentsPage />,
          },
          {
            path: "ai-agents/:id",
            element: <AiAgentDetailPage />,
          },
          {
            path: "ai-agents/:id/calls",
            element: <AiAgentCallsPage />,
          },
          {
            path: "ai-marketplace",
            element: <AiMarketplacePage />,
          },
          {
            path: "ai-marketplace/:listingId",
            element: <AiMarketplaceListingPage />,
          },
          {
            path: "workflows",
            element: <WorkflowsPage />,
          },
          {
            path: "workflows/new",
            element: <WorkflowsPage />,
          },
          {
            path: "workflows/:id",
            element: <WorkflowDetailPage />,
          },
          {
            path: "campaigns",
            element: <CampaignListPage />,
          },
          {
            path: "campaigns/new",
            element: <CreateCampaignPage />,
          },
          {
            path: "campaigns/:id/edit",
            element: <EditCampaignPage />,
          },
          {
            path: "campaigns/:id/live",
            element: <AiCampaignLivePage />,
          },
          {
            path: "power-dialer",
            element: (
              <AddOnProtectedRoute
                addOns={[AddOnType.POWER_DIALER_SINGLE, AddOnType.POWER_DIALER_MULTI]}
                title="Power Dialer"
                description="Blast through call lists with up to 3 lines per agent. Power Dialer is an add-on."
                price="From $25/seat/mo"
              >
                <Outlet />
              </AddOnProtectedRoute>
            ),
            children: [
              {
                index: true,
                element: <PowerDialerPage />,
              },
              {
                path: "monitor",
                element: <LiveMonitorPage />,
              },
              {
                path: "reports",
                element: <DialerReportsPage />,
              },
              {
                path: ":campaignId",
                element: <PowerDialerPage />,
              },
            ],
          },
          {
            path: "payment-success",
            element: <PaymentSuccess />,
          },
          {
            path: "payment-failed",
            element: <PaymentFailed />,
          },
          {
            path: "/manage-organisation",
            children: [
              {
                path: "settings",
                element: <OrganisationSettings />,
              },
              {
                path: "settings/export",
                element: <ExportPage />,
              },
              {
                path: "telephony",
                element: <TelephonyPage />,
              },
              {
                path: "tags",
                element: <TagsSettingsPage />,
              },
              {
                path: "deal-stages",
                element: <DealStagesSettingsPage />,
              },
              {
                path: "call-flows/create",
                element: <CreateCallFlow />,
              },
              {
                path: "call-flows/:id/edit",
                element: <EditCallFlow />,
              },
              {
                path: "team",
                element: <TeamMembers />,
              },
              {
                path: "plans",
                element: <Navigate to="/manage-organisation/billing/plan" replace />,
              },
              {
                path: "billing",
                element: <BillingLayout />,
                children: [
                  {
                    path: "overview",
                    element: <BillingOverview />,
                  },
                  {
                    path: "plan",
                    element: <PlanTab />,
                  },
                  {
                    path: "usage",
                    element: <UsageTab />,
                  },
                  {
                    element: <BillingHistoryLayout />,
                    children: [
                      {
                        path: "invoices",
                        element: <Invoices />,
                      },
                      {
                        path: "transactions",
                        element: <TransactionHistory />,
                      },
                    ],
                  },
                  {
                    path: "add-ons",
                    element: <AddOns />,
                  },
                ],
              },
            ],
          },
          {
            path: "/settings/organisations",
            element: <Organisations />,
          },
          {
            path: "/settings/organisations/new",
            element: <NewOrganisation />,
          },
        ],
      },
          ],
      },
        ],
      },
    ],
  },
  {
    element: <PlatformAdminRoute />,
    children: [
      {
        path: "/platform-admin",
        element: <PlatformAdminLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="/platform-admin/organisations" replace />,
          },
          {
            path: "organisations",
            element: <OrganisationsList />,
          },
          {
            path: "organisations/:id",
            element: <OrganisationDetailPage />,
          },
          {
            path: "workflow-runs/:id",
            element: <WorkflowRunDetailPage />,
          },
          {
            path: "access-requests",
            element: <AccessRequestsList />,
          },
          {
            path: "a2p-registrations",
            element: <A2pRegistrationsQueue />,
          },
          {
            path: "tax-exemptions",
            element: <TaxExemptionsQueue />,
          },
          {
            path: "email-blocklist",
            element: <EmailBlocklistPage />,
          },
          {
            path: "calling-rules",
            element: <CallingRulesPage />,
          },
          {
            path: "fraud-signals",
            element: <FraudSignalsPage />,
          },
          {
            path: "charges",
            element: <OrganisationCharges />,
          },
          {
            path: "activity",
            element: <PlatformActivityPage />,
          },
          {
            path: "logs",
            element: <LiveLogs />,
          },
          {
            path: "users",
            element: <UsersList />,
          },
          {
            path: "phone-numbers",
            element: <PhoneNumbersList />,
          },
          {
            path: "pricing",
            element: <PricingManagement />,
          },
          {
            path: "pricing/plans/new",
            element: <EditPricingPlan />,
          },
          {
            path: "pricing/plans/:id",
            element: <EditPricingPlan />,
          },
          {
            path: "ai-agents",
            element: <AiAgentsManagement />,
            children: [
              { index: true, element: <Navigate to="templates" replace /> },
              { path: "prompts", element: <PromptsAdmin key="AGENT" kind="AGENT" /> },
              { path: "skills", element: <PromptsAdmin key="SKILL" kind="SKILL" /> },
              { path: "templates", element: <AiTemplatesAdmin /> },
              { path: "listings", element: <AiListingsAdmin /> },
              { path: "deployed", element: <DeployedAgentsAdmin /> },
              { path: "test", element: <AiTestTriggers /> },
              { path: "performance", element: <AiPerformanceDashboard /> },
            ],
          },
          {
            path: "ai-agents/new",
            element: <AiAgentWizard />,
          },
          {
            path: "ai-agents/new/:templateId",
            element: <AiAgentWizard />,
          },
          {
            path: "ai-agents/templates/new",
            element: <EditAiTemplate />,
          },
          {
            path: "ai-agents/templates/:id",
            element: <EditAiTemplate />,
          },
          {
            path: "ai-agents/deployed/:agentId",
            element: <ConfigureDeployedAgent />,
          },
          {
            path: "analytics",
            element: <Analytics />,
          },
          // Legacy standalone paths — now tabs under AI Agents. Redirect for old bookmarks.
          {
            path: "ai-test-triggers",
            element: <Navigate to="/platform-admin/ai-agents/test" replace />,
          },
          {
            path: "ai-performance",
            element: <Navigate to="/platform-admin/ai-agents/performance" replace />,
          },
          {
            path: "settings",
            element: <PlatformSettings />,
          },
        ],
      },
    ],
  },
]);

export default AppNavigation;
